import { addMonths, format } from "date-fns";
import {
  TesSalaryTableCell,
  TesSalaryTableDocument,
  TesSalaryTableDraftContent,
} from "../../tesSalaryTables/tesSalaryTablesApi";

export const EffectiveDateFormat = "yyyy-MM-dd"
export const emptyTesSalaryTableDraft = (): TesSalaryTableDraftContent => ({
  name: "",
  effectiveDate: format(new Date(), EffectiveDateFormat),
  monthlyDivider: "",
  workTitleIds: [],
  rows: [
    {
      rowId: "r1",
      label: "",
      secondaryLabel: "",
      sortOrder: 0,
    },
  ],
  columns: [
    {
      columnId: "c1",
      label: "",
      secondaryLabel: "",
      sortOrder: 0,
    },
  ],
  cells: [],
});

export const nextCompactId = (prefix: "r" | "c", ids: string[]) => {
  const currentMax = ids.reduce((result, id) => {
    const match = id.match(new RegExp(`^${prefix}(\\d+)$`));
    if (!match) return result;
    return Math.max(result, Number(match[1]));
  }, 0);

  return `${prefix}${currentMax + 1}`;
};

export const getNextLabelFromLast = (items: Array<{ label: string }>) => {
  const lastLabel = items[items.length - 1]?.label || "";
  const lastMatch = lastLabel.match(/^(.*?)(\d+)$/);

  if (!lastMatch) return lastLabel;

  return `${lastMatch[1]}${Number(lastMatch[2]) + 1}`;
};

export const toRoundedString = (value: number, scale = 2) =>
  Number.isFinite(value) ? value.toFixed(scale) : "";

export const toDraftContent = (
  table?: TesSalaryTableDocument | null,
): TesSalaryTableDraftContent => {
  if (!table) return emptyTesSalaryTableDraft();
  const ret = {
    name: table.name,
    effectiveDate: format(addMonths(table.effectiveDate, 1), EffectiveDateFormat),
    monthlyDivider: table.monthlyDivider || "",
    workTitleIds: table.workTitleIds || [],
    rows: table.rows.map((row) => ({
      ...row,
      secondaryLabel: row.secondaryLabel || "",
    })),
    columns: table.columns.map((column) => ({
      ...column,
      secondaryLabel: column.secondaryLabel || "",
    })),
    cells: table.cells.map((cell) => ({
      ...cell,
      hourlySalary: cell.hourlySalary || "",
      monthlySalary: cell.monthlySalary || "",
    })),
  };
  return ret;
};

export const normalizeDraftForComparison = (
  content: TesSalaryTableDraftContent,
): TesSalaryTableDraftContent => ({
  ...content,
  monthlyDivider: content.monthlyDivider || "",
  workTitleIds: [...content.workTitleIds],
  rows: content.rows.map((row, index) => ({
    ...row,
    secondaryLabel: row.secondaryLabel || "",
    sortOrder: index,
  })),
  columns: content.columns.map((column, index) => ({
    ...column,
    secondaryLabel: column.secondaryLabel || "",
    sortOrder: index,
  })),
  cells: content.cells.map((cell) => ({
    ...cell,
    hourlySalary: cell.hourlySalary || "",
    monthlySalary: cell.monthlySalary || "",
  })),
});

export const getTesSalaryTableDraftCell = (
  cells: TesSalaryTableCell[],
  rowId: string,
  columnId: string,
) => cells.find((cell) => cell.rowId === rowId && cell.columnId === columnId);

export const upsertTesSalaryTableDraftCell = ({
  draft,
  rowId,
  columnId,
  field,
  value,
  monthlyDivider,
  monthlySalaryScale = 2,
}: {
  draft: TesSalaryTableDraftContent;
  rowId: string;
  columnId: string;
  field: "hourlySalary" | "monthlySalary";
  value: string;
  monthlyDivider?: number | null;
  monthlySalaryScale?: number;
}): TesSalaryTableDraftContent => {
  const existingCell = getTesSalaryTableDraftCell(draft.cells, rowId, columnId);
  const nextCell = existingCell
    ? { ...existingCell, [field]: value }
    : {
        rowId,
        columnId,
        hourlySalary: "",
        monthlySalary: "",
        [field]: value,
      };

  if (monthlyDivider && Number.isFinite(monthlyDivider) && monthlyDivider > 0) {
    if (field === "hourlySalary" && value !== "") {
      const hourly = Number(value);
      if (Number.isFinite(hourly)) {
        nextCell.monthlySalary = toRoundedString(
          hourly * monthlyDivider,
          monthlySalaryScale,
        );
      }
    }

    if (field === "monthlySalary" && value !== "") {
      const monthly = Number(value);
      if (Number.isFinite(monthly)) {
        nextCell.hourlySalary = toRoundedString(monthly / monthlyDivider);
      }
    }
  }

  const nextCells = existingCell
    ? draft.cells.map((cell) =>
        cell.rowId === rowId && cell.columnId === columnId ? nextCell : cell,
      )
    : [...draft.cells, nextCell];

  return { ...draft, cells: nextCells };
};

export const removeDraftRow = (
  draft: TesSalaryTableDraftContent,
  rowId: string,
): TesSalaryTableDraftContent => ({
  ...draft,
  rows: draft.rows.filter((row) => row.rowId !== rowId),
  cells: draft.cells.filter((cell) => cell.rowId !== rowId),
});

export const removeDraftColumn = (
  draft: TesSalaryTableDraftContent,
  columnId: string,
): TesSalaryTableDraftContent => ({
  ...draft,
  columns: draft.columns.filter((column) => column.columnId !== columnId),
  cells: draft.cells.filter((cell) => cell.columnId !== columnId),
});

export const reorderDraftRows = ({
  draft,
  sourceRowId,
  targetRowId,
  position,
}: {
  draft: TesSalaryTableDraftContent;
  sourceRowId: string;
  targetRowId: string;
  position: "before" | "after";
}): TesSalaryTableDraftContent => {
  if (sourceRowId === targetRowId) return draft;

  const sourceIndex = draft.rows.findIndex((row) => row.rowId === sourceRowId);
  const targetIndex = draft.rows.findIndex((row) => row.rowId === targetRowId);

  if (sourceIndex < 0 || targetIndex < 0) return draft;

  const withoutSource = draft.rows.filter((row) => row.rowId !== sourceRowId);
  const baseTargetIndex = withoutSource.findIndex(
    (row) => row.rowId === targetRowId,
  );

  if (baseTargetIndex < 0) return draft;

  const insertIndex =
    position === "before" ? baseTargetIndex : baseTargetIndex + 1;
  const movedRow = draft.rows[sourceIndex];
  const nextRows = [...withoutSource];
  nextRows.splice(insertIndex, 0, movedRow);

  return { ...draft, rows: nextRows };
};

export const reorderDraftColumns = ({
  draft,
  sourceColumnId,
  targetColumnId,
  position,
}: {
  draft: TesSalaryTableDraftContent;
  sourceColumnId: string;
  targetColumnId: string;
  position: "before" | "after";
}): TesSalaryTableDraftContent => {
  if (sourceColumnId === targetColumnId) return draft;

  const sourceIndex = draft.columns.findIndex(
    (column) => column.columnId === sourceColumnId,
  );
  const targetIndex = draft.columns.findIndex(
    (column) => column.columnId === targetColumnId,
  );

  if (sourceIndex < 0 || targetIndex < 0) return draft;

  const withoutSource = draft.columns.filter(
    (column) => column.columnId !== sourceColumnId,
  );
  const baseTargetIndex = withoutSource.findIndex(
    (column) => column.columnId === targetColumnId,
  );

  if (baseTargetIndex < 0) return draft;

  const insertIndex =
    position === "before" ? baseTargetIndex : baseTargetIndex + 1;
  const movedColumn = draft.columns[sourceIndex];
  const nextColumns = [...withoutSource];
  nextColumns.splice(insertIndex, 0, movedColumn);

  return { ...draft, columns: nextColumns };
};
